import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle, Award, Briefcase, Landmark, BookOpen } from "lucide-react";

export default function About() {
  const bookingUrl = "https://academy.suhasinibasa.com/web/checkout/6a8eb2e3570da12b73781f39";

  return (
    <div className="relative bg-obsidian text-slate-100 min-h-screen">
      <div className="noise-overlay" />

      {/* Header Banner */}
      <section className="relative py-20 lg:py-28 px-4 sm:px-6 lg:px-12 border-b border-white/5 overflow-hidden">
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-gold/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-4xl mx-auto text-left space-y-6 relative z-10">
          <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">THE INSTRUCTOR</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-tight">
            Learn from someone who has been inside the room.
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 leading-relaxed font-normal">
            Suhasini Basa is a premium Career Transformation Coach with over 16 years of hands-on experience in IT systems, software delivery, and financial-services business lines.
          </p>
        </div>
      </section>

      {/* Narrative Section */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-12 bg-navy relative border-b border-white/5">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          
          <div className="lg:col-span-5 space-y-6">
            <div className="border border-white/10 p-3 bg-obsidian">
              <img 
                src="/images/suhasini-basa-portrait.webp" 
                alt="Suhasini Basa - Career Transformation Coach Portrait" 
                className="w-full h-[400px] object-cover"
              />
            </div>
            <div className="bg-obsidian border border-white/5 p-6 space-y-2">
              <span className="font-mono text-xs text-gold uppercase tracking-wider font-bold">VOUCHED CREDENTIALS</span>
              <p className="text-sm text-slate-300">16 Years IT &amp; Capital Markets Practitioner</p>
              <p className="text-sm text-slate-300">Specialist in Treasury &amp; Trade Finance Systems</p>
              <p className="text-sm text-slate-300">PMP &amp; Agile Enterprise Delivery Expert</p>
            </div>
          </div>

          <div className="lg:col-span-7 space-y-8">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">
              My Professional Journey
            </h2>
            
            <div className="space-y-6 text-slate-300 text-base leading-relaxed">
              <p>
                I spent years working inside complex technology and financial-services environments, moving through roles in software development, business analysis and project management. That experience taught me something important: <strong>technical and delivery skills alone do not determine career growth.</strong>
              </p>
              <p>
                As a practitioner, I handled everything from system requirements, database modeling, trade flow validations, and system integration testing, to UAT sign-offs and PMO reporting. I operated under high stakes, helping deliver systems for multinational banking groups, investments divisions, and capital markets.
              </p>
              <p>
                I quickly noticed that many brilliant Business Analysts and Project Managers were completely invisible to the leaders deciding promotions and budget allocations. They worked late hours, wrote perfect functional documents, yet their contributions remained unseen.
              </p>
              <p>
                That is why I created this Career Influencer Hub. Career acceleration isn’t about waiting for permissions; it’s about learning to demonstrate impact, communicate with absolute authority, manage difficult stakeholder friction, and position yourself as a strategic owner.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-6 border-t border-white/5">
              <div className="flex items-start space-x-3">
                <Briefcase className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-white font-bold text-sm">Pragmatic Delivery</h4>
                  <p className="text-xs text-slate-400 mt-1">Grounding advice in actual software implementation, not general wellness jargon.</p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <Landmark className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-white font-bold text-sm">Financial Core Context</h4>
                  <p className="text-xs text-slate-400 mt-1">Direct familiarity with high-pressure Treasury, Trade Finance, and Investment structures.</p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* 6 Step Philosophy */}
      <section className="py-16 lg:py-24 px-4 sm:px-6 lg:px-12 bg-obsidian">
        <div className="max-w-7xl mx-auto text-center space-y-12">
          <div className="max-w-2xl mx-auto space-y-4">
            <span className="text-xs uppercase tracking-[0.25em] text-gold font-mono font-bold">THE BLUEPRINT</span>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-white">The Six-Part Transformation Framework</h2>
            <p className="text-slate-400 text-sm sm:text-base">
              Every curriculum inside the Suhasini Basa ecosystem aligns with these six logical pillars of growth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            {[
              { title: "1. Confidence", desc: "Build an unshakable state of professional presence to speak up in executive forums." },
              { title: "2. Communication", desc: "Speak directly in high-level business metrics and translate execution into ROI." },
              { title: "3. Influence", desc: "Successfully guide project priority alignment across teams without formal authority." },
              { title: "4. Visibility", desc: "Systematically highlight individual and team contributions to senior managers." },
              { title: "5. Leadership", desc: "Adopt the mindset of a strategic partner instead of an execution-based order-taker." },
              { title: "6. Career Growth", desc: "Position your experience to command double-digit promotional hikes." }
            ].map((step, idx) => (
              <div key={idx} className="bg-navy border border-white/5 p-6 space-y-3">
                <h4 className="font-display text-lg font-bold text-white">{step.title}</h4>
                <p className="text-slate-400 text-sm leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>

          <div className="pt-8">
            <a
              href={bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="about-cta-book"
              className="bg-gold hover:bg-gold-hover text-obsidian px-8 py-4 font-display font-bold text-sm uppercase tracking-widest inline-flex items-center gap-2"
            >
              Book a Strategy Call
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
